import React from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BiLoaderCircle } from 'react-icons/bi';
import { useChangePasswordMutation } from '@/store/api/authApi';
import { changePasswordSchema, ChangePasswordFormData } from '@/utils/validations';
import CustomInput from '@/components/common/CustomInput';
import { Icons } from '@/utils/Icons';
import { toast } from 'react-toastify';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  setShowChangePasswordModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const { control, handleSubmit, reset } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await changePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }).unwrap();
      toast.success('Password changed successfully!');
      reset();
      onClose();
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to change password.');
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 px-4">
      <div
        className="relative w-full max-w-[400px] rounded-[10px] border-[3px] border-[#ff9500] bg-modelBg p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-black hover:text-red-500"
        >
          <Icons.Cross2 size={12} />
        </button>

        <h2 className="mb-4 text-center text-base font-semibold text-black">Change Password</h2>
        <div className="mb-4 border-b-2 border-black" />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <CustomInput
            name="oldPassword"
            control={control}
            label="Old Password"
            type="password"
            placeholder="Enter your old password"
          />
          <CustomInput
            name="newPassword"
            control={control}
            label="New Password"
            type="password"
            placeholder="Enter new password"
          />
          <CustomInput
            name="confirmPassword"
            control={control}
            label="Confirm Password"
            type="password"
            placeholder="Re-enter new password"
          />

          <button
            type="submit"
            disabled={isLoading}
            className="mt-2 flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-orange-500 to-orange-400 py-2.5 font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
          >
            {isLoading ? <BiLoaderCircle className="animate-spin" size={20} /> : 'Change Password'}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
